import React, { useContext, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { fetchInsights } from "../AdminPanel/InsightManagement";
import { useInsightsWs } from "../CoTrader/Utilities/CoTraderUtils";
import { TimeSince } from "../CoTrader/Utilities/relativeTime";
import {
  clampScore,
  formatScore,
  getQuestion,
  getSentiment,
  getUpdateDisplay,
} from "../CoTrader/Utilities/updateDisplay";
import { UserDashboardContext } from "../ProtectedRoute";
import HomeChat from "./HomeChat";
import { HOME_CHAT_THEME } from "./homeChatTheme";
import { useAssistant } from "../Common/AssistantHost";
import { InsightsSkeleton, UpdatesSkeleton } from "./HomeSkeleton";
import { getHomeUpdateKey, useHomeUpdatesFeed } from "./useHomeUpdatesFeed";
import "./Home.css";
import { useTheme } from "../../ThemeProvider/ThemeContext";

const HOME_UPDATES_SYMBOL = "HOME";
const HOME_UPDATES_LANGUAGE = "English";
const FEATURED_INSIGHTS_LIMIT = 3;
const INSIGHTS_URL = "https://www.emalpha.com/insights";
const INSIGHTS_REFRESH_MS = 5 * 60 * 1000;
const CLOCK_TICK_MS = 60 * 1000;
const DEFAULT_FOLLOW_UP_QUESTION =
  "What is the highest-conviction macro risk on the desk right now?";
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const TONE_ICONS = {
  "Risk-off": "fa-shield-halved",
  Constructive: "fa-arrow-trend-up",
  Balanced: "fa-scale-balanced",
  Monitoring: "fa-hourglass-half",
};

const MARKET_TONE_THRESHOLD = 0.18;

const UPDATE_FILTERS = [
  { key: "all", label: "All" },
  { key: "negative", label: "Negative" },
  { key: "neutral", label: "Neutral" },
  { key: "positive", label: "Positive" },
];

const TONE_LABELS = {
  negative: "Negative",
  neutral: "Neutral",
  positive: "Positive",
};

const formatInsightDate = (value) => {
  if (!value) return "";

  // Date-only strings parse as UTC midnight; format them in UTC so they do
  // not shift back a day for users west of UTC.
  const isDateOnly = typeof value === "string" && DATE_ONLY_PATTERN.test(value);
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(value);
  }

  return parsedDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    ...(isDateOnly ? { timeZone: "UTC" } : {}),
  });
};

const getGreeting = (date) => {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const getImpactStats = (updates = []) => {
  const scores = updates
    .filter((update) => update?.type !== "count_down")
    .map((update) => update?.impact_score)
    .filter((score) => score != null && score !== "")
    .map(Number)
    .filter((score) => Number.isFinite(score));

  if (!scores.length) return { average: null, count: 0 };

  return {
    average: scores.reduce((sum, score) => sum + score, 0) / scores.length,
    count: scores.length,
  };
};

const getMarketToneSummary = (updates = []) => {
  const { average, count } = getImpactStats(updates);

  if (average == null) {
    return {
      label: "Monitoring",
      note: "Waiting for a clearer directional skew from the live brief.",
      average,
      count,
    };
  }

  if (average <= -MARKET_TONE_THRESHOLD) {
    return {
      label: "Risk-off",
      note: "Live themes are leaning defensive across the desk.",
      average,
      count,
    };
  }

  if (average >= MARKET_TONE_THRESHOLD) {
    return {
      label: "Constructive",
      note: "Live signals are tilting toward supportive market conditions.",
      average,
      count,
    };
  }

  return {
    label: "Balanced",
    note: "Signals are mixed, with no dominant macro impulse yet.",
    average,
    count,
  };
};

// The lead card asks about the theme itself, never the update's own
// follow-up question, so it cannot duplicate the Next Question card.
const getLeadThemePrompt = (update) =>
  `Brief me on "${update.title}": what is driving it and how could it affect markets?`;

const SentimentBadge = ({ sentiment }) => {
  if (!sentiment) return null;
  return (
    <span className={`hm-sentiment hm-sentiment--${sentiment.tone}`}>
      {sentiment.label}
      {sentiment.score ? <span>{sentiment.score}</span> : null}
    </span>
  );
};

const Home = () => {
  const { isDark } = useTheme();
  const { tabs } = useContext(UserDashboardContext);
  const assistant = useAssistant();
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [updateFilter, setUpdateFilter] = useState("all");
  // Re-render once a minute so relative times and the greeting stay current.
  const [nowMs, setNowMs] = useState(() => Date.now());
  const homeUpdatesSymbolRef = useRef(HOME_UPDATES_SYMBOL);
  const homeChatRef = useRef(null);
  const {
    insights: updates,
    loading: updatesLoading,
    hasPendingIncoming,
    lastReceivedAt,
  } = useInsightsWs(
    HOME_UPDATES_SYMBOL,
    3,
    homeUpdatesSymbolRef,
    false,
    HOME_UPDATES_LANGUAGE,
  );
  const {
    displayUpdates,
    showLoading: showUpdatesLoading,
    showEmptyState: showUpdatesEmptyState,
  } = useHomeUpdatesFeed(updates, updatesLoading, {
    hasPendingIncoming,
    lastReceivedAt,
    enableRotation: false,
  });
  const updateRows = displayUpdates.map((update, index) => ({
    update,
    key: getHomeUpdateKey(update, index),
    display: getUpdateDisplay(update, nowMs),
    sentiment: update.type === "count_down" ? null : getSentiment(update),
  }));
  const filterCounts = updateRows.reduce(
    (counts, { sentiment }) => {
      if (sentiment) counts[sentiment.tone] = (counts[sentiment.tone] || 0) + 1;
      return counts;
    },
    { all: updateRows.length },
  );
  const visibleUpdateRows =
    updateFilter === "all"
      ? updateRows
      : updateRows.filter(({ sentiment }) => sentiment?.tone === updateFilter);
  const featuredInsights = insights.slice(0, FEATURED_INSIGHTS_LIMIT);
  const marketTone = getMarketToneSummary(displayUpdates);
  const leadUpdate = displayUpdates[0] || null;
  const leadSentiment = getSentiment(leadUpdate);
  const followUpQuestion =
    displayUpdates.map(getQuestion).find(Boolean) || DEFAULT_FOLLOW_UP_QUESTION;
  const tonePosition =
    marketTone.average == null ? 50 : ((clampScore(marketTone.average) + 1) / 2) * 100;
  const now = new Date(nowMs);
  const todayLabel = now.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const askQuestion = (questionText) =>
    homeChatRef.current?.askQuestion(questionText);

  useEffect(() => {
    const timerId = window.setInterval(() => setNowMs(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Home stays mounted across tab switches, so refresh quietly in the
    // background; only the first load shows the loading state.
    const getHomeInsights = async ({ silent }) => {
      if (!silent) setInsightsLoading(true);
      try {
        const list = await fetchInsights();
        if (!cancelled) setInsights(list.insight_details || []);
      } catch (error) {
        console.error("Unable to fetch home insights:", error);
        if (!silent && !cancelled) setInsights([]);
      } finally {
        if (!silent && !cancelled) setInsightsLoading(false);
      }
    };

    void getHomeInsights({ silent: false });
    const timerId = window.setInterval(
      () => void getHomeInsights({ silent: true }),
      INSIGHTS_REFRESH_MS,
    );
    return () => {
      cancelled = true;
      window.clearInterval(timerId);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Home | EMAlpha</title>
      </Helmet>
      <div className="hm-page">
        <div className="hm-shell">
          <header>
            <h1 className="hm-title">{getGreeting(now)}</h1>
            <p className="hm-subtitle">{todayLabel}</p>
          </header>

          <section className="hm-card hm-ask" aria-labelledby="hm-ask-title">
            <h2 className="hm-ask-title" id="hm-ask-title">
              Ask EMAlpha about markets
            </h2>
            <HomeChat
              ref={homeChatRef}
              origin="FXGPT"
              defaultCompanyIds={[""]}
              defaultDashboardName=""
              defaultCountries={[""]}
              expandedHeight="min(780px, calc(100vh - 144px))"
              expandedPresentation="overlay"
              expandedOverlayTop={112}
              expandedOverlayWidth="min(1240px, calc(100vw - 120px))"
              showSuggestionsDefault={true}
              themeStyles={isDark ? undefined : HOME_CHAT_THEME}
              useApiSuggestionsOnly
              availableTabs={tabs}
              onDelegateSend={
                assistant
                  ? ({ userInput, evidenceRefs }) =>
                      assistant.ask(userInput, evidenceRefs)
                  : undefined
              }
            />
          </section>

          <section className="hm-brief-grid" aria-label="Desk brief">
            <div className="hm-brief hm-brief--tone">
              <div className="hm-brief-head">
                <div>
                  <div className="hm-label">Market Tone</div>
                  <div className="hm-brief-value">{marketTone.label}</div>
                </div>
                <div className="hm-brief-icon" aria-hidden="true">
                  <i className={`fa-solid ${TONE_ICONS[marketTone.label]}`} />
                </div>
              </div>
              <div className="hm-brief-note">{marketTone.note}</div>
              <div className="hm-meter">
                <div className="hm-meter-track" aria-hidden="true">
                  {marketTone.average != null && (
                    <div
                      className="hm-meter-marker"
                      style={{ left: `${tonePosition}%` }}
                    />
                  )}
                </div>
                <div className="hm-meter-scale">
                  <div>
                    <strong>{formatScore(-1)}</strong>
                    <span>Bearish</span>
                  </div>
                  <div className="hm-meter-scale-mid">
                    {marketTone.average == null ? (
                      <span>No scored updates</span>
                    ) : (
                      <>
                        <strong>Avg impact {formatScore(marketTone.average)}</strong>
                        <span>
                          {marketTone.count}{" "}
                          {marketTone.count === 1 ? "update" : "updates"}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="hm-meter-scale-end">
                    <strong>{formatScore(1)}</strong>
                    <span>Bullish</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="hm-brief"
              disabled={!leadUpdate}
              onClick={() => leadUpdate && askQuestion(getLeadThemePrompt(leadUpdate))}
            >
              <div className="hm-brief-head">
                <div>
                  <div className="hm-label">Lead Theme</div>
                  <div className="hm-brief-value">
                    {leadUpdate?.title || "Awaiting the next live theme"}
                  </div>
                </div>
                <div className="hm-brief-icon" aria-hidden="true">
                  <i className="fa-solid fa-chart-line" />
                </div>
              </div>
              <div className="hm-brief-note">
                {leadUpdate?.description ||
                  "The top-ranked live update will anchor this slot."}
              </div>
              {/* Footer slot stays mounted (hidden when empty) so the card
                  keeps its height when the lead update comes and goes. */}
              <div
                className="hm-brief-foot"
                style={leadUpdate ? undefined : { visibility: "hidden" }}
                aria-hidden={leadUpdate ? undefined : true}
              >
                <span>
                  Brief me on this
                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </span>
                <SentimentBadge sentiment={leadSentiment} />
              </div>
            </button>

            <button
              type="button"
              className="hm-brief hm-brief--question"
              title={followUpQuestion}
              onClick={() => askQuestion(followUpQuestion)}
            >
              <div className="hm-brief-head">
                <div>
                  <div className="hm-label">Next Question</div>
                  <div className="hm-brief-value">{followUpQuestion}</div>
                </div>
                <div className="hm-brief-icon" aria-hidden="true">
                  <i className="fa-regular fa-comment" />
                </div>
              </div>
              <div className="hm-brief-foot">
                <span>
                  Ask in chat
                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </span>
              </div>
            </button>
          </section>

          <section className="hm-panels">
            <div className="hm-card hm-panel">
              <div className="hm-panel-head">
                <h2 className="hm-panel-title">Market Updates</h2>
                <div className="hm-filter" role="group" aria-label="Filter by sentiment">
                  {UPDATE_FILTERS.map(({ key, label }) => (
                    <button
                      type="button"
                      key={key}
                      className="hm-filter-btn"
                      aria-pressed={updateFilter === key}
                      onClick={() => setUpdateFilter(key)}
                    >
                      {label}
                      <span className="hm-filter-count">{filterCounts[key] || 0}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="hm-panel-body hm-panel-body--cards">
                {showUpdatesLoading ? (
                  <UpdatesSkeleton />
                ) : visibleUpdateRows.length > 0 ? (
                  visibleUpdateRows.map(({ update, key, display }) => {
                    const question = getQuestion(update);

                    return (
                      <article className={`hm-update hm-update--${display.tone}`} key={key}>
                        <div className="hm-update-head">
                          <span
                            className="hm-update-icon"
                            role="img"
                            aria-label={display.kind}
                            title={display.kind}
                          >
                            <i className={display.icon} aria-hidden="true" />
                          </span>
                          <div className="hm-update-title">
                            {update.title || "Market update"}
                          </div>
                          <TimeSince
                            className="hm-update-time"
                            timestamp={display.timestamp}
                          />
                        </div>
                        {update.description && update.type !== "count_down" && (
                          <div className="hm-update-desc">{update.description}</div>
                        )}
                        {(display.status || question) && (
                          <div className="hm-update-foot">
                            <span className="hm-update-status">{display.status}</span>
                            {question && (
                              <button
                                type="button"
                                className="hm-ask-link"
                                title={question}
                                onClick={() => askQuestion(question)}
                              >
                                Ask follow-up
                                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        )}
                      </article>
                    );
                  })
                ) : updateFilter !== "all" && updateRows.length > 0 ? (
                  <div className="hm-empty">
                    No {TONE_LABELS[updateFilter].toLowerCase()} updates right now.
                  </div>
                ) : showUpdatesEmptyState ? (
                  <div className="hm-empty">Waiting for the next live update...</div>
                ) : null}
              </div>
            </div>

            <div className="hm-card hm-panel">
              <div className="hm-panel-head">
                <h2 className="hm-panel-title">Top Insights</h2>
                <a
                  href={INSIGHTS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hm-panel-link"
                >
                  View all
                  <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                </a>
              </div>
              <div className="hm-panel-body">
                {insightsLoading ? (
                  <InsightsSkeleton />
                ) : featuredInsights.length === 0 ? (
                  <div className="hm-empty">No insights published yet.</div>
                ) : (
                  featuredInsights.map((insight, index) => {
                    // Prefer the current article field. `image` remains
                    // supported for insights created through the legacy API.
                    const imageUrl = insight.cover_image_url || insight.image;

                    return (
                      <a
                        href={insight.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={insight.slug || index}
                        className="hm-insight"
                        aria-label={`Read ${insight.title || "insight"}`}
                      >
                        <div className="hm-insight-media">
                          {imageUrl ? (
                            <img src={imageUrl} alt="" loading="lazy" />
                          ) : (
                            <div className="hm-insight-fallback" aria-hidden="true">
                              <i className="fa-regular fa-newspaper" />
                            </div>
                          )}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className="hm-insight-date">
                            {formatInsightDate(insight.date)}
                          </div>
                          <div className="hm-insight-title">{insight.title}</div>
                          {insight.text && (
                            <div className="hm-insight-text">{insight.text}</div>
                          )}
                          <span className="hm-read-more">
                            Read more
                            <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                          </span>
                        </div>
                      </a>
                    );
                  })
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Home;
